import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { LogOut, Trophy, Flame, Zap, Activity, ChevronRight, Camera, User as UserIcon, TrendingUp, Ticket, Coins, Footprints } from "lucide-react";
import { XPRing } from "../components/Progress";
import BodyMetricsCard from "../components/BodyMetricsCard";
import client, { API, formatErr } from "../lib/api";
import { toast } from "sonner";

const goalLabels = {
    lose_weight: "Lose Weight",
    build_strength: "Build Strength",
    improve_fitness: "Improve Fitness",
    stay_active: "Stay Active",
    challenge_mode: "Challenge Mode",
};

export default function Profile() {
    const { user, logout, refresh } = useAuth();
    const [uploading, setUploading] = useState(false);
    const [photoVer, setPhotoVer] = useState(Date.now());
    const [prs, setPrs] = useState([]);

    useEffect(() => {
        if (user) {
            client.get("/users/me/prs").then((r) => setPrs(r.data));
        }
    }, [user]);

    if (!user) return null;
    const xpForLevel = user.current_level * 500;

    const onPickPhoto = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 8 * 1024 * 1024) {
            toast.error("Max 8MB image");
            return;
        }
        setUploading(true);
        const fd = new FormData();
        fd.append("photo", f);
        try {
            await client.post("/users/me/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
            await refresh();
            setPhotoVer(Date.now());
            toast.success("Profile photo updated");
        } catch (err) {
            toast.error(formatErr(err));
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const photoUrl = user.profile_photo
        ? `${API}/users/${user.id}/photo?v=${photoVer}`
        : null;

    return (
        <div className="px-5 pt-8 pb-8">
            <span className="label-cap text-xs text-neon-orange">Profile</span>

            <div className="mt-4 flex items-center gap-4">
                <label className="relative cursor-pointer group" data-testid="profile-photo-pick">
                    <div
                        className="w-20 h-20 rounded-full overflow-hidden border-2 border-neon-green bg-charcoal-700 flex items-center justify-center"
                        style={{ boxShadow: "0 0 16px rgba(57,255,20,0.25)" }}
                    >
                        {photoUrl ? (
                            <img src={photoUrl} alt="" className="w-full h-full object-cover" />
                        ) : (
                            <UserIcon size={28} className="text-zinc-500" />
                        )}
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-neon-orange flex items-center justify-center group-hover:scale-110 transition">
                        <Camera size={12} className="text-white" />
                    </div>
                    <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={onPickPhoto}
                        disabled={uploading}
                        data-testid="profile-photo-input"
                    />
                </label>
                <div className="flex-1 min-w-0">
                    <h1 className="font-display font-black text-3xl uppercase tracking-tight leading-none">
                        {user.name}
                    </h1>
                    <p className="text-zinc-500 text-sm mt-1 truncate">{user.email}</p>
                    {uploading && <div className="text-xs text-neon-green mt-1 label-cap">Uploading...</div>}
                </div>
            </div>

            <div className="mt-6 surface-2 p-5 flex items-center gap-5">
                <XPRing value={user.xp} max={xpForLevel} size={120} color="#39FF14" label={`/ ${xpForLevel}`} />
                <div className="flex-1">
                    <div className="label-cap text-xs text-neon-green">Level</div>
                    <div className="font-display font-black text-5xl leading-none">{user.current_level}</div>
                    <div className="flex items-center gap-1 mt-2 text-neon-orange">
                        <Flame size={14} />
                        <span className="font-bold text-sm">{user.streak} streak</span>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-2 mt-3">
                <div className="surface-1 p-3">
                    <div className="label-cap text-[10px] text-zinc-500">Goal</div>
                    <div className="font-display font-bold uppercase mt-1">
                        {goalLabels[user.goal] || "Not set"}
                    </div>
                </div>
                <div className="surface-1 p-3">
                    <div className="label-cap text-[10px] text-zinc-500">Badges</div>
                    <div className="font-display font-black text-2xl text-neon-green">
                        {(user.badges || []).length}
                    </div>
                </div>
            </div>

            <BodyMetricsCard />

            {/* Personal Records */}
            <div className="mt-8" data-testid="profile-prs">
                <div className="flex items-center gap-2 mb-3">
                    <TrendingUp size={18} className="text-neon-orange" />
                    <h2 className="font-display font-black text-xl uppercase tracking-tight">Personal Records</h2>
                </div>
                {prs.length === 0 ? (
                    <div className="surface-1 p-5 text-center">
                        <p className="text-zinc-400 text-sm">
                            No PRs yet. Submit a weighted challenge with{" "}
                            <span className="text-neon-orange font-bold">weight + reps</span> to track your e1RM.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-2">
                        {prs.map((p) => (
                            <div
                                key={p.exercise_type}
                                data-testid={`pr-${p.exercise_type}`}
                                className="surface-2 p-3 border-l-2 border-l-neon-orange"
                            >
                                <div className="label-cap text-[10px] text-zinc-500">{p.label}</div>
                                <div className="flex items-baseline gap-1 mt-1">
                                    <span className="font-display font-black text-2xl text-neon-green">
                                        {p.best_1rm}
                                    </span>
                                    <span className="text-xs text-zinc-400 label-cap">kg e1RM</span>
                                </div>
                                <div className="text-[11px] text-zinc-500 mt-1">
                                    Best: {p.best_weight}kg × {p.best_reps}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="mt-6 space-y-2">
                <ProfileLink to="/app/runs" icon={Footprints} title="Run Tracker" />
                <ProfileLink to="/app/badges" icon={Zap} title="My Badges" />
                <ProfileLink to="/app/invites" icon={Ticket} title="My Invites" />
                <ProfileLink to="/app/parleys" icon={Coins} title="Parleys" />
                <ProfileLink to="/app/progress" icon={Activity} title="Progress" />
                <ProfileLink to="/app/leaderboard" icon={Trophy} title="Leaderboard" />
                <ProfileLink to="/app/exercises" icon={Activity} title="Exercise Library" />
                <ProfileLink to="/app/workout-plan" icon={Activity} title="Weekly Plan" />
            </div>

            <button
                onClick={logout}
                data-testid="profile-logout"
                className="mt-6 w-full py-3 surface-1 text-neon-orange font-display font-bold uppercase tracking-wider hover:border-neon-orange flex items-center justify-center gap-2"
            >
                <LogOut size={16} /> Log Out
            </button>

            <div className="mt-6 text-center text-xs text-zinc-600">
                <div className="font-display uppercase tracking-widest">No Excuse · v1.1</div>
                <div className="mt-1">Train smart. Train hard. Stay safe.</div>
            </div>
        </div>
    );
}

function ProfileLink({ to, icon: Icon, title }) {
    return (
        <Link to={to} className="surface-1 p-4 flex items-center gap-3 hover:border-neon-green transition-colors">
            <Icon size={18} className="text-neon-green" />
            <span className="font-display font-bold uppercase text-sm flex-1">{title}</span>
            <ChevronRight size={16} className="text-zinc-500" />
        </Link>
    );
}
